import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/helpers/axios";
import { useAuth } from "@/hooks";
import { IssuesQueryData } from "../_types/issues.types";

export const useMarkIssueAsRead = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      await axiosInstance.patch(`/v2/issues/${id}/read-status`, {
        read: true,
      });
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ["issues"] });

      const previousQueries = queryClient.getQueriesData<IssuesQueryData>({
        queryKey: ["issues"],
      });

      queryClient.setQueriesData<IssuesQueryData>({ queryKey: ["issues"] }, (currentData) => {
        if (!currentData) return currentData;

        const readAt = Date.now();
        const readByName =
          user && [user.firstName, user.lastName].filter(Boolean).join(" ").trim();

        return {
          ...currentData,
          items: currentData.items.map((issue) =>
            issue.id === id
              ? {
                  ...issue,
                  read: true,
                  readByUserId: user?.id ?? null,
                  readByName: readByName || user?.email || null,
                  readAt,
                }
              : issue
          ),
        };
      });

      return { previousQueries };
    },
    onError: (_error, _id, context) => {
      context?.previousQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["issues"] });
    },
  });
};
